import { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  Card,
  Typography,
  Spin,
  Button,
  Row,
  Col,
  message,
  Select,
} from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { AuthContext } from "../../context/AuthContext";

const { Title, Text } = Typography;
const { Option } = Select;

export default function DepartmentWiseSalary() {
  const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [organisations, setOrganisations] = useState([]);

  // Filters same as PayrollSummary
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const [organisationId, setOrganisationId] = useState(1);
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);

  // batching (10 per chart)
  const [currentBatch, setCurrentBatch] = useState(0);
  const batchSize = 10;

  // Fetch organisations
  const fetchOrganisations = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setOrganisations(res.data.results || []);
    } catch (err) {
      console.error("Error fetching organisations:", err);
    }
  };

  // Fetch salary data
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${backUrl}/api/reports/department-salary/?organisation_id=${organisationId}&month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setData(res.data || []);
      setCurrentBatch(0);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch department salary summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisations();
  }, []);

  useEffect(() => {
    if (organisationId) fetchData();
  }, [organisationId, month, year]);

  // Pagination logic
  const startIndex = currentBatch * batchSize;
  const endIndex = startIndex + batchSize;
  const currentData = data.slice(startIndex, endIndex);

  const handleNext = () => {
    if (endIndex < data.length) setCurrentBatch(currentBatch + 1);
  };

  const handlePrev = () => {
    if (currentBatch > 0) setCurrentBatch(currentBatch - 1);
  };

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <div className="p-6 bg-gray-50 flex flex-col justify-start items-center min-h-[500px]">
      {/* Filters same as PayrollSummary */}
      <div className="flex gap-4 mb-6">
        <Select
          value={organisationId}
          onChange={(value) => setOrganisationId(value)}
          style={{ width: 280 }}
        >
          {organisations.map((org) => (
            <Option key={org.id} value={org.id}>
              {org.name}
            </Option>
          ))}
        </Select>

        <Select
          value={month}
          onChange={(value) => setMonth(value)}
          style={{ width: 120 }}
        >
          {months.map((m, index) => (
            <Option key={index + 1} value={index + 1}>
              {m}
            </Option>
          ))}
        </Select>

        <Select
          value={year}
          onChange={(value) => setYear(value)}
          style={{ width: 120 }}
        >
          {years.map((y) => (
            <Option key={y} value={y}>
              {y}
            </Option>
          ))}
        </Select>
      </div>

      {/* Chart section */}
      <Card
        style={{
          margin: 20,
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          padding: 16,
          width: "90%",
          maxWidth: 900,
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
          <Col>
            <Title level={4}>Department-wise Salary Summary</Title>
            <Text type="secondary">
              {`Month: ${months[month - 1]} | Year: ${year}`}
            </Text>
          </Col>
        </Row>

        {loading ? (
          <Spin tip="Loading data..." style={{ width: "100%" }} />
        ) : data.length === 0 ? (
          <Title level={5} type="secondary" style={{ textAlign: "center" }}>
            No salary data available for this period.
          </Title>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={currentData}
                margin={{ top: 10, right: 30, left: 0, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="department_name"
                  angle={-25}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis />
                <Tooltip formatter={(val) => `₹${val.toLocaleString()}`} />
                <Bar dataKey="total_cost" fill="#1890ff" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            <Row justify="center" style={{ marginTop: 20 }}>
              <Button
                onClick={handlePrev}
                disabled={currentBatch === 0}
                style={{ marginRight: 10 }}
              >
                Previous
              </Button>
              <Button
                onClick={handleNext}
                disabled={endIndex >= data.length}
              >
                Next
              </Button>
            </Row>

            <Row justify="center" style={{ marginTop: 8 }}>
              <Text type="secondary">
                Showing {startIndex + 1}–{Math.min(endIndex, data.length)} of {data.length} departments
              </Text>
            </Row>
          </>
        )}
      </Card>
    </div>
  );
}
